import {
  CONSENT_COOKIE_CLEARED_MAX_AGE_SECONDS,
  CONSENT_COOKIE_MAX_AGE_SECONDS,
  CONSENT_COOKIE_NAME,
} from "./constants"
import { GTAG_COOKIE_NAME_PREFIX } from "./vendors/gtag"

// Decisão de consentimento LGPD. Opt-in afirmativo: default "unknown" ⇒ nada
// carrega/dispara; só "granted" habilita a analítica.
export type ConsentDecision = "granted" | "denied" | "unknown"

const CONSENT_GRANTED: ConsentDecision = "granted"
const CONSENT_DENIED: ConsentDecision = "denied"
const CONSENT_UNKNOWN: ConsentDecision = "unknown"

// Gramática do `document.cookie` e dos nomes de domínio (zero chars mágicos no meio do código).
const COOKIE_ENTRY_SEPARATOR = ";"
const COOKIE_VALUE_SEPARATOR = "="
const DOMAIN_LABEL_SEPARATOR = "."
// Um domínio-pai só existe a partir de duas etiquetas ("exemplo.com"); "localhost" não tem.
const MIN_LABELS_FOR_PARENT_DOMAIN = 2

/**
 * Cookies que o Google grava no browser desta loja, por PREFIXO.
 *
 * `_ga` (id de cliente, ~2 anos) e `_ga_<id>` (estado da sessão) são os do GA4; `_gid`/`_gat`
 * (janela curta) e `_gac_`/`_gcl_` (ligação a campanhas do Google Ads) aparecem consoante as
 * funcionalidades ativas na propriedade. Deixar qualquer um vivo depois da retirada é o mesmo
 * defeito: consentimento formalmente revogado com o terceiro ainda a reconhecer o visitante.
 */
const VENDOR_COOKIE_PREFIXES = [
  GTAG_COOKIE_NAME_PREFIX,
  "_gid",
  "_gat",
  "_gac_",
  "_gcl_",
] as const

/**
 * Parse fail-closed: só "granted"/"denied" exatos são aceites; qualquer outro valor
 * (lixo, vazio, ausente, caixa diferente) vira "unknown". Nunca lança.
 */
export function parseConsentDecision(raw?: string): ConsentDecision {
  if (raw === CONSENT_GRANTED) {
    return CONSENT_GRANTED
  }
  if (raw === CONSENT_DENIED) {
    return CONSENT_DENIED
  }
  return CONSENT_UNKNOWN
}

/**
 * Serializa o cookie de consentimento: Path=/ (todas as rotas), Max-Age (~180d),
 * SameSite=Lax (não é cross-site — evita envio em navegação de terceiros).
 */
export function serializeConsentCookie(decision: ConsentDecision): string {
  return serializeCookie(decision, CONSENT_COOKIE_MAX_AGE_SECONDS)
}

/**
 * Cookie que APAGA a decisão (Story 10.10 — direito de retirada da LGPD). Mesmos atributos
 * do cookie de gravação (o browser só substitui um cookie quando nome+Path+domínio batem
 * certo) com `Max-Age=0`, que o expira de imediato ⇒ o estado volta a "unknown" e o banner
 * reaparece.
 */
export function serializeClearedConsentCookie(): string {
  return serializeCookie("", CONSENT_COOKIE_CLEARED_MAX_AGE_SECONDS)
}

function serializeCookie(value: string, maxAgeSeconds: number): string {
  return [
    `${CONSENT_COOKIE_NAME}=${value}`,
    "Path=/",
    `Max-Age=${maxAgeSeconds}`,
    "SameSite=Lax",
    // Cookie de consentimento/tracking sempre sobre HTTPS (produção = Traefik websecure).
    "Secure",
  ].join("; ")
}

/**
 * Apaga a decisão no browser. Casca sobre `serializeClearedConsentCookie` (a parte pura e
 * testada); SSR-safe por `typeof document`.
 */
export function clearConsentCookie(): void {
  if (typeof document === "undefined") {
    return
  }
  document.cookie = serializeClearedConsentCookie()
}

/**
 * A nova decisão obriga a recarregar a página?
 *
 * Consentir INJETA o `<Script>` do terceiro; retirar o consentimento não o desinjeta — o
 * script fica no DOM (e ativo) até uma navegação completa. Por isso, quando já há tag
 * carregado e a decisão nova é "denied", só um `reload` cumpre a promessa de que "recusar
 * ⇒ nenhum script de vendor no DOM".
 *
 * A decisão ANTERIOR não entra na assinatura: quem chama é o banner, que só existe enquanto o
 * consentimento é "unknown" — comparar com a anterior seria um ramo inatingível a fingir-se
 * de guarda.
 */
export function shouldReloadAfterDecision(
  next: ConsentDecision,
  hasLoadedTag: boolean
): boolean {
  return hasLoadedTag && next === CONSENT_DENIED
}

/**
 * Nomes dos cookies do VENDOR presentes na string dada (ver `VENDOR_COOKIE_PREFIXES`).
 * Puro e testável — a casca impura é `clearVendorCookies`.
 */
export function listVendorCookieNames(cookieString: string): string[] {
  const names: string[] = []
  for (const entry of cookieString.split(COOKIE_ENTRY_SEPARATOR)) {
    const separatorIndex = entry.indexOf(COOKIE_VALUE_SEPARATOR)
    const name = (
      separatorIndex === -1 ? entry : entry.slice(0, separatorIndex)
    ).trim()
    if (isVendorCookieName(name) && !names.includes(name)) {
      names.push(name)
    }
  }
  return names
}

/**
 * Cookie que EXPIRA um cookie de terceiro pelo nome. Sem `SameSite`/`Secure` (o do vendor não
 * os tem, e para apagar só nome+Path+Domain têm de bater certo); `Domain` é opcional porque o
 * mesmo nome pode existir em duas variantes — ver `clearVendorCookies`.
 */
export function serializeClearedCookie(name: string, domain?: string): string {
  const attributes = [
    `${name}=`,
    "Path=/",
    `Max-Age=${CONSENT_COOKIE_CLEARED_MAX_AGE_SECONDS}`,
  ]
  if (domain) {
    attributes.push(`Domain=${domain}`)
  }
  return attributes.join("; ")
}

/**
 * TODOS os domínios-pai em que o `gtag.js` pode ter gravado, do mais curto ao mais longo
 * (`loja.exemplo.com` ⇒ `.exemplo.com`, `.loja.exemplo.com`). Vazio quando não há domínio a que
 * subir (`localhost`), caso em que só a variante host-only faz sentido.
 *
 * São TODOS e não só as duas últimas etiquetas por causa dos SUFIXOS PÚBLICOS compostos: num
 * `loja.com.br` — plausível numa loja brasileira — `.com.br` é um sufixo público, o browser
 * recusa a escrita em silêncio e a limpeza falhava sem deixar sinal nenhum. Tentar cada nível é
 * mais barato do que carregar uma lista de sufixos públicos: um domínio inválido é ignorado
 * pelo browser, um válido apaga.
 */
export function listVendorCookieDomains(hostname: string): string[] {
  const labels = hostname.split(DOMAIN_LABEL_SEPARATOR)
  const domains: string[] = []
  for (let size = MIN_LABELS_FOR_PARENT_DOMAIN; size <= labels.length; size++) {
    domains.push(
      `${DOMAIN_LABEL_SEPARATOR}${labels.slice(-size).join(DOMAIN_LABEL_SEPARATOR)}`
    )
  }
  return domains
}

/**
 * Apaga os cookies do vendor (Story 10.10 — a retirada tem de RETIRAR alguma coisa).
 *
 * Sem isto, "Gerenciar cookies" apagava só o cookie da casa e deixava os `_ga`/`_ga_<id>` do
 * Google vivos por ~2 anos: consentimento formalmente revogado com o terceiro ainda a
 * identificar o visitante. Best-effort por natureza — o cookie pode ter sido gravado no
 * domínio de topo ou no host, e nenhuma das tentativas pode rebentar a página.
 */
export function clearVendorCookies(): void {
  if (typeof document === "undefined") {
    return
  }
  try {
    const domains = readParentCookieDomains()
    for (const name of listVendorCookieNames(document.cookie)) {
      document.cookie = serializeClearedCookie(name)
      for (const domain of domains) {
        document.cookie = serializeClearedCookie(name, domain)
      }
    }
  } catch {
    // Apagar cookies é best-effort: um `document.cookie` que recuse a escrita não pode
    // derrubar a página em que o cliente acabou de retirar o consentimento.
  }
}

function readParentCookieDomains(): string[] {
  if (typeof window === "undefined") {
    return []
  }
  const hostname = window.location?.hostname
  return typeof hostname === "string" ? listVendorCookieDomains(hostname) : []
}

function isVendorCookieName(name: string): boolean {
  return VENDOR_COOKIE_PREFIXES.some((prefix) => name.startsWith(prefix))
}

export function hasAnalyticsConsent(decision: ConsentDecision): boolean {
  return decision === CONSENT_GRANTED
}

/**
 * Lê o consentimento do document.cookie. SSR-safe: no server (sem document) devolve
 * "unknown" fail-closed. Guardado por typeof — não toca document no topo do módulo.
 */
export function readConsentFromDocument(): ConsentDecision {
  if (typeof document === "undefined") {
    return CONSENT_UNKNOWN
  }
  return parseConsentDecision(readCookie(document.cookie, CONSENT_COOKIE_NAME))
}

// Extrai o valor cru de um cookie da string document.cookie; undefined se ausente. Exportado
// para teste direto (o caminho de extração é curto-circuitado sob env node via
// readConsentFromDocument, logo só é exercitável aqui). NÃO faz decodeURIComponent: os valores
// são um enum fixo sem chars especiais e serializeConsentCookie não codifica — decodificar
// seria assimétrico E lançaria URIError num cookie com `%` malformado (violando "Nunca lança",
// que num efeito de root layout derrubaria a loja inteira). parseConsentDecision já fecha
// qualquer valor não-enum em "unknown".
export function readCookie(cookieString: string, name: string): string | undefined {
  for (const entry of cookieString.split(COOKIE_ENTRY_SEPARATOR)) {
    const separatorIndex = entry.indexOf(COOKIE_VALUE_SEPARATOR)
    if (separatorIndex === -1) {
      continue
    }
    if (entry.slice(0, separatorIndex).trim() === name) {
      return entry.slice(separatorIndex + 1).trim()
    }
  }
  return undefined
}
